import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation } from "@apollo/client";

import { QUERY_TEST_ACTIVE_DIRECTORY_APP_ONLY } from "../../../../config/graphql/query";
import { SWITCH_ACTIVE_DIRECTORY_TO_APP_ONLY } from "../../../../config/graphql/mutation";

import { getApiUrl } from "../../../../lib/common/getApiUrl";

interface SwitchToAppOnlyProps {
  clientId: string;
  activeDirectory: IActiveDirectory;
}

// Entra needs up to a minute to spread a fresh consent: retried quietly.
const AUTO_RETRIES = 6;
const AUTO_RETRY_MS = 10000;

/** Counterpart of `Outlook/SwitchToAppOnly`; the organisation id is prefilled. */
const SwitchToAppOnly = React.memo(
  ({ clientId, activeDirectory }: SwitchToAppOnlyProps) => {
    const { t } = useTranslation(["client", "common"]);

    const [visible, setVisible] = useState(false);
    const [tenantId, setTenantId] = useState(
      activeDirectory.homeTenantId ?? "",
    );
    const [tested, setTested] =
      useState<IActiveDirectoryAppOnlyTestResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [consentGranted, setConsentGranted] = useState(false);

    const autoTested = useRef(false);
    const autoRetries = useRef(0);

    const history = useHistory();
    const location = useLocation();

    const apiUrl = getApiUrl();

    useEffect(() => {
      if (activeDirectory.homeTenantId && !tenantId) {
        setTenantId(activeDirectory.homeTenantId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDirectory.homeTenantId]);

    const consentUrl = useMemo(
      () =>
        qs.stringifyUrl(
          {
            url: `${apiUrl}/api/v1/activedirectory/auth/admin-consent`,
            query: {
              client: clientId,
              tenantId: tenantId.trim(),
              redirectUri: window.location.href,
            },
          },
          { encode: true },
        ),
      [apiUrl, clientId, tenantId],
    );

    const [runTest, { loading: testing }] = useLazyQuery<{
      testActiveDirectoryAppOnly: IActiveDirectoryAppOnlyTestResult;
    }>(QUERY_TEST_ACTIVE_DIRECTORY_APP_ONLY, {
      fetchPolicy: "network-only",
      context: { headers: { ClientId: clientId } },
      onCompleted: ({ testActiveDirectoryAppOnly }) => {
        setTested(testActiveDirectoryAppOnly);
        setError(
          testActiveDirectoryAppOnly.ok
            ? null
            : testActiveDirectoryAppOnly.message ?? null,
        );
      },
      onError: (e) => {
        setTested(null);
        setError(e.message);
      },
    });

    const [runSwitch, { loading: switching }] = useMutation(
      SWITCH_ACTIVE_DIRECTORY_TO_APP_ONLY,
      {
        refetchQueries: ["activeDirectory"],
        context: { headers: { ClientId: clientId } },
        onCompleted: () => {
          toast.success<string>(
            t("client:client.integrations.activeDirectory.appOnly.switch.done"),
          );
          setVisible(false);
        },
        onError: (e) => {
          // A failed attempt voids the earlier test.
          setTested(null);
          setError(e.message);
        },
      },
    );

    const onChangeTenant = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setTenantId(event.target.value);
        setTested(null);
        setError(null);
      },
      [],
    );

    const onClose = useCallback(() => {
      setVisible(false);
      setTested(null);
      setError(null);
      setConsentGranted(false);
    }, []);

    // Back from the consent screen: reopen with the tenant from the URL.
    useEffect(() => {
      const params = qs.parse(location.search);

      const granted = params.activeDirectoryAdminConsent === "granted";
      const refused = typeof params.error === "string" ? params.error : null;

      if (!granted && !refused) {
        return;
      }

      if (typeof params.tenantId === "string") {
        setTenantId(params.tenantId);
      }

      setConsentGranted(granted);
      setError(refused);
      setVisible(true);
      autoTested.current = false;
      autoRetries.current = 0;

      history.replace(location.pathname);
    }, [location.search, location.pathname, history]);

    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        error ||
        autoTested.current ||
        !tenantId.trim()
      ) {
        return;
      }

      autoTested.current = true;
      runTest({ variables: { tenantId: tenantId.trim() } });
    }, [visible, consentGranted, error, tenantId, runTest]);

    // Same lag as a fresh connection — see ConnectAppOnlyModal.
    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        !tested ||
        tested.ok ||
        autoRetries.current >= AUTO_RETRIES
      ) {
        return undefined;
      }

      autoRetries.current += 1;

      const timer = window.setTimeout(() => {
        runTest({ variables: { tenantId: tenantId.trim() } });
      }, AUTO_RETRY_MS);

      return () => window.clearTimeout(timer);
    }, [visible, consentGranted, tested, tenantId, runTest]);

    // No red while the quiet retries still run.
    const autoChecking =
      consentGranted &&
      !tested?.ok &&
      (testing || (!!tested && autoRetries.current < AUTO_RETRIES));

    if (activeDirectory.authMode === "appOnly") {
      return null;
    }

    return (
      <>
        <Button variant="outline-primary" onClick={() => setVisible(true)}>
          {t(
            "client:client.integrations.activeDirectory.appOnly.switch.button",
          )}
        </Button>

        <Modal show={visible} onHide={onClose} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {t(
                "client:client.integrations.activeDirectory.appOnly.switch.title",
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              {t(
                "client:client.integrations.activeDirectory.appOnly.switch.intro",
                { email: activeDirectory.email },
              )}
            </p>

            {/* Stated plainly: "switch" sounds like it could lose data, and the
                first question anyone asks is what happens to the companies that
                are already being synchronized. */}
            <Alert variant="info" className="small py-2 mb-2">
              {t(
                "client:client.integrations.activeDirectory.appOnly.switch.keeps",
              )}
            </Alert>

            <Form.Group>
              <Form.Label>
                {t(
                  "client:client.integrations.activeDirectory.appOnly.field.tenantId",
                )}
              </Form.Label>
              <Form.Control
                value={tenantId}
                onChange={onChangeTenant}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
              <Form.Text className="text-muted">
                {t(
                  "client:client.integrations.activeDirectory.appOnly.switch.tenantPrefilled",
                )}
              </Form.Text>
            </Form.Group>

            <div className="d-flex align-items-center mb-3">
              <a
                href={consentUrl}
                className={`btn btn-outline-primary mr-3 text-nowrap flex-shrink-0 ${
                  tenantId.trim() ? "" : "disabled"
                }`}
              >
                {t(
                  "client:client.integrations.activeDirectory.appOnly.button.consent",
                )}
              </a>
              <small className="text-muted">
                {t(
                  "client:client.integrations.activeDirectory.appOnly.button.consentHelp",
                )}
              </small>
            </div>

            {autoChecking && (
              <Alert variant="info" className="small py-2 mb-2">
                {t(
                  "client:client.integrations.activeDirectory.appOnly.consentReturned",
                )}
              </Alert>
            )}

            {tested?.ok && (
              <Alert variant="success">
                {t("client:client.integrations.activeDirectory.appOnly.tested")}
              </Alert>
            )}

            {error && !autoChecking && <Alert variant="danger">{error}</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button
              variant="outline-primary"
              disabled={!tenantId.trim() || testing}
              onClick={() => {
                setError(null);
                runTest({ variables: { tenantId: tenantId.trim() } });
              }}
            >
              {testing && (
                <Spinner animation="border" size="sm" className="mr-2" />
              )}
              {t(
                "client:client.integrations.activeDirectory.appOnly.button.test",
              )}
            </Button>
            <Button
              variant="primary"
              disabled={!tested?.ok || switching}
              onClick={() => {
                setError(null);
                runSwitch({
                  variables: {
                    input: {
                      id: activeDirectory.id,
                      tenantId: tenantId.trim(),
                    },
                  },
                });
              }}
            >
              {switching && (
                <Spinner animation="border" size="sm" className="mr-2" />
              )}
              {t(
                "client:client.integrations.activeDirectory.appOnly.switch.confirm",
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  },
);

export default SwitchToAppOnly;
